import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { CreditCard, Eye, Receipt, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import FilterToolbar from '../../component/filter/FilterToolbar';
import DefaultTable from '../../component/table/DefaultTable';
import StatusChip from '../../component/common/StatusChip';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import PaymentModal from './paymentmodal/PaymentModal';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileBilling from '../../component/serviceorder/MobileBilling';

// Faturamento de OS — ordens concluídas (status "Finalizada" = aguardando
// pagamento, "Paga" = já faturada). A API devolve as duas com `status=Concluida`
// (ver `Repository.FindByQuery`). O pagamento reaproveita o `PaymentModal`
// usado em `service_order/List.js`.
const STATUS_OPTIONS = ['Finalizada', 'Paga'];

function billingStatusLabel(value) {
    return value === 'Paga' ? 'Faturada' : value;
}

const TB_FIELD_RADIUS = '9px';

const TbLabel = ({ children }) => (
    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
        {children}
    </Typography>
);

export default function Billing() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();

    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [appliedPeriod, setAppliedPeriod] = useState({ start: dayjs().startOf('month'), end: dayjs().endOf('month') });
    const [orders, setOrders] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
    // Mobile: ordens marcadas para faturar e fila de pagamento (o pagamento segue sendo
    // por ordem, no mesmo PaymentModal; ao concluir uma, abre a próxima da fila).
    const [selectedIds, setSelectedIds] = useState([]);
    const [billingQueue, setBillingQueue] = useState([]);
    const [mobileCustomer, setMobileCustomer] = useState('');

    const fetchOrders = async (start, end) => {
        setLoading(true);
        try {
            const response = await httpClient.get(
                `/orderservices?status=Concluida&startDate=${start?.format('DD/MM/YYYY') || ''}&endDate=${end?.format('DD/MM/YYYY') || ''}`
            );
            setOrders(Array.isArray(response.data) ? response.data : []);
            setAppliedPeriod({ start, end });
        } catch (error) {
            console.error('Erro ao buscar ordens de serviço:', error);
            setMessage('Erro ao buscar ordens de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(startDate, endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        if (!startDate?.isValid?.() || !endDate?.isValid?.()) return;
        fetchOrders(startDate, endDate);
    };

    const term = search.trim().toLowerCase();
    const filteredData = orders.filter((order) => {
        if (selectedStatus && order.status !== selectedStatus) return false;
        if (mobileCustomer && order.customer_name !== mobileCustomer) return false;
        if (term) {
            return (
                String(order.number ?? '').toLowerCase().includes(term) ||
                String(order.customer_name ?? '').toLowerCase().includes(term)
            );
        }
        return true;
    });

    const toggleSelected = (orderId) => setSelectedIds((prev) => (prev.includes(orderId) ? prev.filter((x) => x !== orderId) : [...prev, orderId]));

    const handleStartBilling = () => {
        const queue = filteredData.filter((order) => order.status === 'Finalizada' && selectedIds.includes(order.order_service_id));
        if (queue.length === 0) return;
        setBillingQueue(queue);
        setSelectedOrderForPayment(queue[0]);
        setOpenPaymentModal(true);
    };

    const pendingCount = filteredData.filter((order) => order.status === 'Finalizada').length;

    const columns = [
        {
            path: 'number',
            name: 'Número',
            invisible: false,
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row.number}
                </Typography>
            ),
        },
        { path: 'customer_name', name: 'Cliente', invisible: false },
        {
            path: 'created_at',
            name: 'Criada em',
            invisible: false,
            render: (row) => (row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY') : '--'),
        },
        {
            path: 'amount',
            name: 'Valor',
            invisible: false,
            render: (row) => `R$ ${row.amount ?? '0,00'}`,
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => <StatusChip domain="orderService" value={row.status} />,
        },
    ];

    const actions = [
        {
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => navigate(`/serviceorders/${row.order_service_id}/view`),
                tooltip: 'Visualizar',
                cursor: 'pointer',
                sx: { mr: 1 },
            },
        },
        ...(hasPermission('serviceorder.bill') ? [{
            icon: <CreditCard size={16} strokeWidth={2} color={theme.palette.secondary.main} />,
            props: {
                onClick: (row) => {
                    setSelectedOrderForPayment(row);
                    setOpenPaymentModal(true);
                },
                tooltip: 'Receber Pagamento',
                style: { cursor: 'pointer' },
            },
            ruleShow: 'Finalizada',
        }] : []),
    ];

    const paymentModal = selectedOrderForPayment && (
        <PaymentModal
            key={selectedOrderForPayment.order_service_id}
            open={openPaymentModal}
            onClose={() => {
                setOpenPaymentModal(false);
                setSelectedOrderForPayment(null);
                setBillingQueue([]);
            }}
            total={selectedOrderForPayment.amount || 0}
            entityId={selectedOrderForPayment.order_service_id}
            entityType="service_order"
            onFinish={() => {
                fetchOrders(startDate, endDate);
                setMessage('Pagamento processado com sucesso!');
                setSeverity('success');
                setOpenSnackbar(true);
                const paidId = selectedOrderForPayment.order_service_id;
                setSelectedIds((prev) => prev.filter((x) => x !== paidId));
                const rest = billingQueue.filter((order) => order.order_service_id !== paidId);
                setBillingQueue(rest);
                if (rest.length > 0) {
                    setSelectedOrderForPayment(rest[0]);
                    return;
                }
                setOpenPaymentModal(false);
                setSelectedOrderForPayment(null);
            }}
        />
    );

    // Shell mobile: ordens em cartões com seleção, do protótipo `ordens/faturamento.html`.
    if (isMobileShell) {
        const customerNames = [...new Set(orders.map((order) => order.customer_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
        return (
            <PageScaffold links={[]} titlePageTarget={'Faturamento de OS'} setOpenSnackbar={setOpenSnackbar} setMessage={setMessage} openSnackbar={openSnackbar} severity={severity} message={message}>
                <MobileBilling
                    orders={filteredData}
                    loading={loading}
                    customers={customerNames}
                    filters={{ customer: mobileCustomer, status: selectedStatus, start: startDate, end: endDate }}
                    onApplyFilters={({ customer, status, start, end }) => {
                        setMobileCustomer(customer);
                        setSelectedStatus(status);
                        setStartDate(start);
                        setEndDate(end);
                        if (start?.isValid?.() && end?.isValid?.()) fetchOrders(start, end);
                    }}
                    onClearFilters={() => {
                        const start = dayjs().startOf('month');
                        const end = dayjs().endOf('month');
                        setMobileCustomer('');
                        setSelectedStatus('');
                        setSearch('');
                        setStartDate(start);
                        setEndDate(end);
                        fetchOrders(start, end);
                    }}
                    selectedIds={selectedIds}
                    onToggle={toggleSelected}
                    canBill={hasPermission('serviceorder.bill')}
                    onBill={handleStartBilling}
                    billing={openPaymentModal}
                />
                {paymentModal}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Faturamento de OS'} setOpenSnackbar={setOpenSnackbar} setMessage={setMessage} openSnackbar={openSnackbar} severity={severity} message={message}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: (t) => t.palette.gradient.brand,
                    }}>
                        <Receipt size={18} strokeWidth={2} />
                    </Box>
                    <Box>
                        <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            Faturamento de OS
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            Ordens de serviço concluídas e finalizadas, prontas para faturar.
                        </Typography>
                    </Box>
                </Box>

                {/* No mobile os filtros ficam recolhidos atrás do botão "Filtros"
                    (mesmo padrão de Minhas Vendas); desktop inalterado. */}
                <FilterToolbar
                    plain
                    activeCount={selectedStatus ? 1 : 0}
                    onClear={() => { setSelectedStatus(''); setSearch(''); }}
                    onApply={handleSearch}
                    primary={(
                        <Box sx={{ flex: '0 0 220px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Número ou cliente…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            {(selectedStatus || search) && (
                                <Button
                                    variant="text"
                                    onClick={() => { setSelectedStatus(''); setSearch(''); }}
                                    sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                                >
                                    Limpar
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleSearch}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box sx={{ minWidth: 170 }}>
                            <TbLabel>Status</TbLabel>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedStatus}
                                    displayEmpty
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    renderValue={(value) => billingStatusLabel(value) || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {STATUS_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option === 'Finalizada' ? 'Finalizada (a faturar)' : 'Faturada'}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <TbLabel>Período</TbLabel>
                            <DesktopPeriodSelector
                                start={startDate}
                                end={endDate}
                                onChange={(start, end) => { setStartDate(start); setEndDate(end); }}
                                showRefresh={false}
                                showShortcuts={false}
                                dualMonth
                                align="start"
                            />
                        </Box>

                </FilterToolbar>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                    <Chip
                        label={`Período: ${appliedPeriod.start?.format('DD/MM/YYYY')} – ${appliedPeriod.end?.format('DD/MM/YYYY')}`}
                        size="small"
                        sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                    />
                    {selectedStatus && (
                        <Chip
                            label={`Status: ${billingStatusLabel(selectedStatus)}`}
                            size="small"
                            onDelete={() => setSelectedStatus('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={() => setSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${filteredData.length} ordem${filteredData.length === 1 ? '' : 's'} encontrada${filteredData.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`${pendingCount} a faturar`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                </Stack>

                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={(row) => navigate(`/serviceorders/${row.order_service_id}/view`)}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="ordens"
                    sortableFields={['number', 'customer_name', 'status']}
                    defaultSort={{ field: 'number', direction: 'desc' }}
                />
            </Box>

            {paymentModal}
        </PageScaffold>
    );
}
